import type { CashAccountBalance, CashTransfer } from '../api/cash';
import { formatDate } from '../format/dates';
import { formatAmount } from '../format/money';
import { canAct } from '../session/capability';
import type { AccessibleBranch, Session } from '../session/types';

/**
 * Move money — the contra voucher's rules for the website's page and the phone's screen (Cash &
 * bank step 3, `docs/Cash_Bank_Flow_Design.md` §3.3).
 *
 * <p>
 * <strong>Every refusal here is the server's, mirrored</strong>: two different accounts, both still
 * taking money, an amount above zero, a day not yet come. <strong>Overdrawing is a warning, never a
 * refusal</strong> (owner, 2026-10-22): the book showing less than is being banked means a receipt is
 * missing, and the shop is still holding the notes.
 * </p>
 */

export interface TransferDraft {
  fromId: number | null;
  /** An account at this branch — unused when {@link toBranchId} names another branch. */
  toId: number | null;
  /**
   * Another branch the money is sent to (step 5), or null to move it within this one. The receiving
   * branch chooses its own account when it presses Received.
   */
  toBranchId: number | null;
  /** As typed. */
  amount: string;
  /** `YYYY-MM-DD`. */
  sentOn: string;
  /** The deposit slip number, the UTR. */
  reference: string;
}

/** Money sent to another branch — the company administrator's. */
export interface SendCashTransferInput {
  fromAccountId: number;
  toBranchId: number;
  amount: number;
  sentOn: string;
  reference: string | null;
  notes: string | null;
}

export interface CreateCashTransferInput {
  fromAccountId: number;
  toAccountId: number;
  amount: number;
  sentOn: string;
  reference: string | null;
  notes: string | null;
}

/** The accounts money can move between: every one still taking money, as the position lists them. */
export function movableAccounts(
  accounts: readonly CashAccountBalance[] | undefined,
): CashAccountBalance[] {
  return (accounts ?? []).filter((account) => account.isActive);
}

/**
 * Where a move starts: the till into the bank — the evening's deposit, which is most of what this
 * page is for. Either end is left empty when the branch has no such account.
 */
export function emptyTransfer(
  today: string,
  accounts: readonly CashAccountBalance[] | undefined,
): TransferDraft {
  const movable = movableAccounts(accounts);
  const till = movable.find((a) => a.kind === 'Till' && a.isDefault) ?? null;
  const bank = movable.find((a) => a.kind === 'Bank' && a.isDefault) ?? null;

  return {
    fromId: till?.id ?? null,
    toId: bank?.id ?? null,
    toBranchId: null,
    amount: '',
    sentOn: today,
    reference: '',
  };
}

/**
 * The branches money can be sent to: every other branch this person can act at, and only when they
 * hold `BranchCashTransfer` (owner, 2026-10-22: the company administrator's).
 */
export function otherBranches(session: Session): AccessibleBranch[] {
  if (!canAct(session, 'BranchCashTransfer:Create')) {
    return [];
  }

  return session.user.branches.filter((branch) => branch.id !== session.branch?.id);
}

/** A typed amount as a number, or null when it is not one above zero with at most two decimals. */
export function transferAmount(typed: string): number | null {
  const trimmed = typed.trim().replace(/,/g, '');

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return null;
  }

  const value = Number(trimmed);

  return value > 0 ? value : null;
}

/**
 * What to move so the account is left holding its float — "keep ₹2,000" — or null when it has no
 * float set, or holds no more than it.
 */
export function aboveFloat(account: CashAccountBalance | undefined): number | null {
  if (!account || account.floatToKeep === null || account.floatToKeep === undefined) {
    return null;
  }

  const rest = Math.round((account.closing - account.floatToKeep) * 100) / 100;

  return rest > 0 ? rest : null;
}

/** The one thing standing between the form and Move, or null — most-blocking first. */
export function transferProblem(
  draft: TransferDraft,
  accounts: readonly CashAccountBalance[] | undefined,
  today: string,
): string | null {
  const movable = movableAccounts(accounts);
  const toBranch = draft.toBranchId !== null;

  if (!toBranch && movable.length < 2) {
    return 'This branch needs two accounts before money can move between them.';
  }

  if (draft.fromId === null) {
    return 'Choose where the money comes from.';
  }

  if (!toBranch && draft.toId === null) {
    return 'Choose where it goes.';
  }

  if (!toBranch && draft.fromId === draft.toId) {
    return 'Money has to move to a different account.';
  }

  if (
    !movable.some((a) => a.id === draft.fromId) ||
    (!toBranch && !movable.some((a) => a.id === draft.toId))
  ) {
    return 'That account no longer takes money. Choose another.';
  }

  if (transferAmount(draft.amount) === null) {
    return 'Type how much is moving.';
  }

  if (draft.sentOn === '') {
    return 'Say which day it moved.';
  }

  if (draft.sentOn > today) {
    return 'Money cannot move on a day that has not happened yet.';
  }

  return null;
}

/**
 * Said, never refused, when the move takes more than the book says the account holds — "The book
 * says Counter holds ₹3,000."
 */
export function overdrawWarning(
  draft: TransferDraft,
  accounts: readonly CashAccountBalance[] | undefined,
  currency = '₹',
): string | null {
  const from = (accounts ?? []).find((a) => a.id === draft.fromId);
  const amount = transferAmount(draft.amount);

  if (!from || amount === null || amount <= from.closing) {
    return null;
  }

  return `The book says ${from.name} holds ${currency}${formatAmount(from.closing)}. Moving more means a receipt is missing — it will still be recorded.`;
}

/** Both ends, before and after — the lines under the amount. */
export function afterMove(
  draft: TransferDraft,
  accounts: readonly CashAccountBalance[] | undefined,
): { name: string; before: number; after: number }[] {
  const amount = transferAmount(draft.amount) ?? 0;
  const from = (accounts ?? []).find((a) => a.id === draft.fromId);
  const to = (accounts ?? []).find((a) => a.id === draft.toId);

  // Sent to another branch, only this end is known here — the other lands when it is received.
  if (from && draft.toBranchId !== null) {
    return [{ name: from.name, before: from.closing, after: from.closing - amount }];
  }

  if (!from || !to || from.id === to.id) {
    return [];
  }

  return [
    { name: from.name, before: from.closing, after: from.closing - amount },
    { name: to.name, before: to.closing, after: to.closing + amount },
  ];
}

/** The request, or null while there is a problem — so nothing half-checked is ever sent. */
export function transferRequest(
  draft: TransferDraft,
  accounts: readonly CashAccountBalance[] | undefined,
  today: string,
): CreateCashTransferInput | null {
  const amount = transferAmount(draft.amount);

  if (
    transferProblem(draft, accounts, today) !== null ||
    amount === null ||
    draft.fromId === null ||
    draft.toId === null ||
    draft.toBranchId !== null
  ) {
    return null;
  }

  return {
    fromAccountId: draft.fromId,
    toAccountId: draft.toId,
    amount,
    sentOn: draft.sentOn,
    reference: draft.reference.trim() || null,
    notes: null,
  };
}

/** The request to send to another branch, or null while there is a problem or none is chosen. */
export function sendRequest(
  draft: TransferDraft,
  accounts: readonly CashAccountBalance[] | undefined,
  today: string,
): SendCashTransferInput | null {
  const amount = transferAmount(draft.amount);

  if (
    transferProblem(draft, accounts, today) !== null ||
    amount === null ||
    draft.fromId === null ||
    draft.toBranchId === null
  ) {
    return null;
  }

  return {
    fromAccountId: draft.fromId,
    toBranchId: draft.toBranchId,
    amount,
    sentOn: draft.sentOn,
    reference: draft.reference.trim() || null,
    notes: null,
  };
}

/**
 * The line a branch reads about money on the road — "₹20,000 on its way to Morbi Road — sent from
 * Counter on 21/10/2026, not yet received there." and the receiver's "₹20,000 on its way from Rajkot
 * HO, sent 21/10/2026."
 */
export function inTransitLine(
  transfer: CashTransfer,
  side: 'outgoing' | 'incoming',
  currency = '₹',
): string {
  const figure = `${currency}${formatAmount(transfer.amount)}`;
  const day = formatDate(transfer.sentOn);

  return side === 'outgoing'
    ? `${figure} on its way to ${transfer.toBranchName} — sent from ${transfer.fromAccountName} on ${day}, not yet received there.`
    : `${figure} on its way from ${transfer.fromBranchName}, sent ${day}.`;
}

/** The button: "Move ₹16,450". */
export function moveLabel(amount: number | null, currency = '₹'): string {
  return amount === null ? 'Type the amount' : `Move ${currency}${formatAmount(amount)}`;
}

/**
 * After a count: what to bank so the drawer keeps its float — the count page's "deposit the rest".
 * Null when the account has no float set, there is no bank to deposit into, or nothing is above it.
 */
export function depositTheRest(
  counted: number,
  account: CashAccountBalance | undefined,
  accounts: readonly CashAccountBalance[] | undefined,
): { amount: number; keep: number; bank: CashAccountBalance } | null {
  const bank = movableAccounts(accounts).find((a) => a.kind === 'Bank' && a.isDefault);

  if (!account || account.kind !== 'Till' || !bank || account.floatToKeep == null) {
    return null;
  }

  const amount = Math.round((counted - account.floatToKeep) * 100) / 100;

  return amount > 0 ? { amount, keep: account.floatToKeep, bank } : null;
}
